import { removeDuplicates } from '@carboncopyco/editor-utils';
import type { ITextReplaceRule } from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import React, { useEffect, useRef, useState } from 'react';
import { RulesManager } from '../RulesManager';
import { isTextOldValueIsDuplicate } from '../TextReplaceForm/utils';
import { AIManagerContent } from './AIManagerContent';
import { AIResultModal } from './AIResultModal';
import { AIToolHeaderTitle } from './AIToolHeaderTitle';
import { useAIManagerContext } from './AiManagerContextProvider';
import { EditWithAIForm } from './EditWithAIForm';
import type {
  AIManagerProps,
  AiRequestData,
  ITextReplaceResult,
} from './types';

export const AIManager = ({
  isOpen,
  onClose,
  modifications: aiModifications,
  pageUrl,
  deleteModification,
}: AIManagerProps) => {
  const contentRef = useRef<HTMLDivElement>();
  const [isEditScreenVisible, setIsEditScreenVisible] = useBoolean(false);
  const [prompt, setPrompt] = React.useState<string>('');
  const [promptGeneratedName, setPromptGeneratedName] = useState<string>('');
  const [isOpenGenerateModal, setIsOpenGenerateModal] = useBoolean(false);
  const isEmpty = !aiModifications?.length;
  const [isTooltipVisible, setIsTooltipVisible] = useBoolean(false);
  const {
    onAiRequest,
    innerHtml,
    setEditRule,
    editRule,
    textReplaceRules,
    createAiRequest,
    checkIfTheReplaceTextRuleExists,
    getAllSubGroupModifications,
  } = useAIManagerContext();

  useEffect(() => {
    if (isEditScreenVisible) {
      setPrompt('');
    }
  }, [isEditScreenVisible]);

  useEffect(() => {
    if (editRule) {
      setPrompt(editRule.content.prompt);
      setPromptGeneratedName(editRule.content.name);
      setIsOpenGenerateModal.setTrue();
    } else {
      setPrompt('');
      setPromptGeneratedName('');
    }
  }, [editRule]);

  const onSaveData = (text: string) => {
    setPrompt(text);
    setPromptGeneratedName(text);
    setIsOpenGenerateModal.setTrue();
  };

  const checkIfItemReplacementExistInGroup = (item: ITextReplaceResult) => {
    // check that for the same rule group, the old value of new item is not the same as the new value of another old rule replacement
    // should improve performance by not checking all the rules
    // example prevent in the same group to replace "a" with "b" and "b" with "c"
    const groupId = editRule?.content?.id;
    if (!groupId) {
      return false;
    }
    const groupNewValuesReplacement = getAllSubGroupModifications(groupId).map(
      (mod) => (mod?.content as ITextReplaceRule)?.newValue
    );
    return groupNewValuesReplacement.includes(item.original);
  };

  const filterAiResults = (data: ITextReplaceResult[]) => {
    return removeDuplicates(data, 'original').filter(
      (item) =>
        item.original !== item.replace_with &&
        !isTextOldValueIsDuplicate(textReplaceRules, item.original) &&
        !checkIfItemReplacementExistInGroup(item)
    );
  };

  const onGenerate = async () => {
    const { data } = await onAiRequest({
      task_type: 'FNRAssist',
      prompt: prompt,
      assist_data: { html: innerHtml },
    });
    const { rules, group_name } = data as unknown as AiRequestData;
    if (!editRule?.content?.name) setPromptGeneratedName(group_name);
    return filterAiResults(rules);
  };

  const onCloseGenerateModal = () => {
    setIsOpenGenerateModal.setFalse();
    setEditRule(undefined);
  };

  useEffect(() => {
    setIsTooltipVisible.set(
      isOpen && isEmpty && !isEditScreenVisible && !isOpenGenerateModal
    );
  }, [isEmpty, isEditScreenVisible, isOpen, isOpenGenerateModal]);

  return (
    <>
      <RulesManager
        isOpen={isOpen}
        onClose={onClose}
      >
        <AIToolHeaderTitle
          setTooltipVisible={setIsTooltipVisible.set}
          tooltipVisible={isTooltipVisible}
          onAddRule={() => {
            setIsEditScreenVisible.setTrue();
            contentRef.current?.scrollTo({ top: 0 });
          }}
        />
        {isEmpty ? (
          <>
            <EditWithAIForm
              className="mt-5 pt-4"
              onSaveData={onSaveData}
              closeEditScreen={setIsEditScreenVisible.setFalse}
              open={isEditScreenVisible}
              removeExamples={false}
            />
            {!isEditScreenVisible && (
              <RulesManager.EmptyState color="redWithHat">
                <RulesManager.EmptyState.Title label="Replace elements globally with AI." />
                <RulesManager.EmptyState.Subtitle
                  label="Not sure how it works?"
                  linkLabel="Learn more"
                  linkUrl="https://help.demostack.com/articles/ai-wizard"
                />
              </RulesManager.EmptyState>
            )}
          </>
        ) : (
          <RulesManager.Content ref={contentRef}>
            <AIManagerContent
              modifications={aiModifications ?? []}
              pageUrl={pageUrl}
              deleteModification={deleteModification}
              showCreateForm={isEditScreenVisible}
              AICreationForm={
                <EditWithAIForm
                  className="pt-1"
                  onSaveData={onSaveData}
                  closeEditScreen={setIsEditScreenVisible.setFalse}
                  open={isEditScreenVisible}
                  removeExamples={false}
                />
              }
            />
          </RulesManager.Content>
        )}
      </RulesManager>
      {isOpenGenerateModal && (
        <AIResultModal
          setPrompt={setPrompt}
          promptGeneratedName={promptGeneratedName}
          onRegenerate={onGenerate}
          isReadyToGenerate={!!innerHtml}
          prompt={prompt}
          onClose={onCloseGenerateModal}
          open={isOpenGenerateModal}
          editRule={editRule}
          checkIfTheReplaceTextRuleExists={checkIfTheReplaceTextRuleExists}
          createAiRequest={createAiRequest}
        />
      )}
    </>
  );
};
